<template>
  <div class="video-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchVideoData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 内容区域 -->
        <div v-else class="content-wrapper">
          <!-- 视频播放区域 -->
          <div v-if="videosList.length" class="video-player-section">
            <!-- 视频切换按钮组 -->
            <div class="video-switcher">
              <div
                v-for="(video, index) in videosList"
                :key="index"
                class="video-switch-btn"
                :class="{ active: activeVideoIndex === index }"
                @click="switchVideo(index)"
              >
                {{ video.video_name || `视频${index + 1}` }}
              </div>
            </div>

            <!-- 视频播放器 -->
            <div class="video-player-container">
              <div class="video-wrapper">
                <video
                  ref="videoPlayer"
                  class="company-video"
                  :src="currentVideoPath"
                  controls
                  autoplay
                  playsinline
                  preload="metadata"
                  @loadeddata="onVideoLoaded"
                  @error="onVideoError"
                  @ended="onVideoEnded"
                >
                  <source :src="currentVideoPath" type="video/mp4">
                  {{ t('video.browser_not_support') }}
                </video>

                <!-- 视频加载状态 -->
                <div v-if="isVideoLoading" class="video-loading">
                  <div class="loading-spinner"></div>
                  <p class="loading-text">{{ t('video.buffering') }}</p>
                </div>

                <!-- 视频封面（视频加载前显示） -->
                <div v-if="showVideoCover" class="video-cover" @click="playVideo">
                  <div class="cover-content">
                    <div class="play-button">
                      <el-icon size="80" color="white">
                        <VideoPlay />
                      </el-icon>
                    </div>
                    <p class="play-hint">{{ t('video.click_to_play') }}</p>
                  </div>
                </div>

                <!-- 视频控制遮罩 -->
                <div class="video-overlay" @click="togglePlay">
                  <div v-if="!isPlaying" class="play-overlay">
                    <div class="play-button-overlay">
                      <el-icon size="60" color="white">
                        <VideoPlay />
                      </el-icon>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 视频信息 -->
              <div class="video-info">
                <h3 class="video-title">{{ currentVideoTitle }}</h3>
                <div class="video-meta">
                  <div class="meta-item">
                    <el-icon><Clock /></el-icon>
                    <span>{{ videoDuration }}</span>
                  </div>
                  <div class="meta-item">
                    <el-icon><Calendar /></el-icon>
                    <span>{{ currentVideoDate }}</span>
                  </div>
                </div>

                <!-- 播放控制按钮 -->
                <div class="control-buttons">
                  <button
                    class="control-btn"
                    :class="{ active: isPlaying }"
                    @click="togglePlay"
                  >
                    <el-icon>
                      <component :is="isPlaying ? VideoPause : VideoPlay" />
                    </el-icon>
                    <span>{{ isPlaying ? t('video.pause') : t('video.play') }}</span>
                  </button>

                  <button class="control-btn" @click="toggleMute">
                    <el-icon>
                      <component :is="isMuted ? MuteNotification : Bell" />
                    </el-icon>
                    <span>{{ isMuted ? t('video.unmute') : t('video.mute') }}</span>
                  </button>

                  <button class="control-btn" @click="restartVideo">
                    <el-icon><Refresh /></el-icon>
                    <span>{{ t('video.replay') }}</span>
                  </button>

                  <button class="control-btn" @click="toggleFullscreen">
                    <el-icon>
                      <component :is="isFullscreen ? Close : FullScreen" />
                    </el-icon>
                    <span>{{ isFullscreen ? t('video.exit_fullscreen') : t('video.fullscreen') }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- 富文本内容（如果有） -->
            <div
              v-if="richtextHtml && richtextHtml.trim()"
              class="richtext-content"
              v-html="richtextHtml"
            ></div>
          </div>

          <!-- 空状态（当没有视频时显示） -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <el-icon size="80" color="var(--el-color-primary-light-8)">
                <VideoCamera />
              </el-icon>
            </div>
            <h3 class="empty-title">{{ t('common.no_data') }}</h3>
            <p class="empty-description">{{ t('video.no_video') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Warning,
  VideoPlay,
  VideoPause,
  VideoCamera,
  Clock,
  Calendar,
  Bell,
  MuteNotification,
  Refresh,
  FullScreen,
  Close
} from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n
const { locale, t } = useI18n()

// 响应式数据
const pageData = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const videoPlayer = ref(null)

// 视频切换相关
const activeVideoIndex = ref(0) // 当前激活的视频索引
const videosList = ref([]) // 视频列表

// 视频状态
const isPlaying = ref(false)
const isMuted = ref(false)
const isFullscreen = ref(false)
const isVideoLoading = ref(true)
const showVideoCover = ref(true)
const currentTime = ref(0)
const videoDuration = ref('00:00')
const currentVideoTitle = ref('')
const currentVideoDate = ref('')

// 计算属性
const pageTitle = computed(() => {
  return pageData.value?.page_name || t('menu.company_video')
})

const richtextHtml = computed(() => {
  return pageData.value?.content?.richtext || ''
})

// 当前播放的视频路径
const currentVideoPath = computed(() => {
  return videosList.value[activeVideoIndex.value]?.video_url || ''
})

// 获取视频列表
const getVideosList = computed(() => {
  // 兼容新旧格式：优先取videos数组，没有则兼容旧的video_url
  const content = pageData.value?.content || {}
  if (Array.isArray(content.videos) && content.videos.length) {
    return content.videos.filter(video => video?.video_url) // 过滤掉无URL的视频
  } else if (content.video_url) {
    // 兼容旧格式
    return [{
      video_url: content.video_url,
      video_name: content.video_title || t('video.company_video'),
      video_date: content.video_date || ''
    }]
  }
  return []
})

// 视频控制方法
const playVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.play()
    showVideoCover.value = false
  }
}

const pauseVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.pause()
  }
}

const togglePlay = () => {
  if (!videoPlayer.value) return

  if (videoPlayer.value.paused) {
    playVideo()
  } else {
    pauseVideo()
  }
}

const toggleMute = () => {
  if (videoPlayer.value) {
    videoPlayer.value.muted = !videoPlayer.value.muted
    isMuted.value = videoPlayer.value.muted
  }
}

const restartVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.currentTime = 0
    playVideo()
  }
}

const toggleFullscreen = () => {
  if (!videoPlayer.value) return

  if (!document.fullscreenElement) {
    videoPlayer.value.requestFullscreen?.()
      .then(() => {
        isFullscreen.value = true
      })
      .catch(err => {
        console.error('全屏模式失败:', err)
      })
  } else {
    document.exitFullscreen?.()
    isFullscreen.value = false
  }
}

// 切换视频方法
const switchVideo = (index) => {
  if (index === activeVideoIndex.value) return // 点击当前激活的视频，不处理

  // 暂停当前视频
  if (videoPlayer.value) {
    videoPlayer.value.pause()
  }

  // 更新激活索引
  activeVideoIndex.value = index

  // 重置视频状态
  isVideoLoading.value = true
  showVideoCover.value = true
  isPlaying.value = false
  videoDuration.value = '00:00'

  // 设置当前视频信息
  const currentVideo = videosList.value[index]
  currentVideoTitle.value = currentVideo.video_name || `视频${index + 1}`
  currentVideoDate.value = currentVideo.video_date || new Date().toLocaleDateString()

  // 等待DOM更新后加载新视频
  nextTick(() => {
    if (videoPlayer.value) {
      // 重新加载视频
      videoPlayer.value.load()
      // 自动播放新视频
      videoPlayer.value.play().catch(err => {
        console.log('自动播放失败:', err)
      })
    }
  })
}

// 视频事件处理
const onVideoLoaded = () => {
  isVideoLoading.value = false

  // 设置视频时长
  const duration = videoPlayer.value?.duration || 0
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  videoDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  // 设置当前视频标题和日期（如果还没设置）
  if (!currentVideoTitle.value) {
    const currentVideo = videosList.value[activeVideoIndex.value]
    currentVideoTitle.value = currentVideo?.video_name || t('video.company_video')
    currentVideoDate.value = currentVideo?.video_date || new Date().toLocaleDateString()
  }

  // 自动播放
  playVideo()
}

const onVideoError = (event) => {
  console.error('视频加载失败:', event)
  errorMessage.value = t('video.load_failed')
  isVideoLoading.value = false
}

const onVideoEnded = () => {
  isPlaying.value = false
}

// 监听播放状态
const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
}

const handleTimeUpdate = () => {
  if (videoPlayer.value) {
    currentTime.value = videoPlayer.value.currentTime
  }
}

// 全屏变化监听
const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

// 获取页面数据
const fetchVideoData = async () => {
  isLoading.value = true
  errorMessage.value = ''
  showVideoCover.value = true

  try {
    // 使用当前的语言
    const currentLocale = locale.value

    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/company-video'
    })

    if (response && response.content) {
      pageData.value = response
      // 更新视频列表
      videosList.value = getVideosList.value
      // 重置激活索引
      activeVideoIndex.value = 0

      // 设置初始视频信息
      const firstVideo = videosList.value[0]
      if (firstVideo) {
        currentVideoTitle.value = firstVideo.video_name || t('video.company_video')
        currentVideoDate.value = firstVideo.video_date || new Date().toLocaleDateString()
      }
    } else {
      pageData.value = null
      videosList.value = []
    }
  } catch (error) {
    console.error('获取视频数据失败:', error)
    errorMessage.value = t('common.load_failed')
    videosList.value = []
  } finally {
    isLoading.value = false
  }
}

// 组件挂载时获取数据
onMounted(() => {
  fetchVideoData()

  // 添加全屏变化监听
  document.addEventListener('fullscreenchange', handleFullscreenChange)
})

// 组件卸载时清理
onUnmounted(() => {
  if (videoPlayer.value) {
    videoPlayer.value.pause()
    videoPlayer.value = null
  }

  // 移除事件监听
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
})

// 监听语言变化，重新加载数据
watch(locale, () => {
  fetchVideoData()
})

// 监听视频播放器引用
watch(videoPlayer, (newPlayer) => {
  if (newPlayer) {
    newPlayer.addEventListener('play', handlePlay)
    newPlayer.addEventListener('pause', handlePause)
    newPlayer.addEventListener('timeupdate', handleTimeUpdate)
  }
}, { immediate: true })

// 监听视频列表变化
watch(getVideosList, (newList) => {
  videosList.value = newList
  // 如果当前激活索引超出范围，重置为0
  if (activeVideoIndex.value >= newList.length && newList.length > 0) {
    activeVideoIndex.value = 0
  }
}, { immediate: true, deep: true })
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';

.video-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f9fbf7 0%, #ffffff 100%);
}
// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  /* 背景核心优化：居中 + 覆盖 + 固定（可选） + 轻微模糊 */
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  /* 高斯模糊：使用伪元素实现（避免模糊影响内容），模糊值 2px（极轻微） */
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  /* 伪元素实现背景模糊（不影响文字/内容） */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit; /* 继承父元素背景图 */
    filter: blur(4px); /* 极轻微高斯模糊，可调整（1-3px 都很淡） */
    z-index: 1; /* 放在内容下方 */
    transform: scale(1.01); /* 消除模糊边缘白边 */
  }

  /* 确保内容层级高于模糊层 */
  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }

  @media (max-width: 480px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  /* 浅白色文字阴影：极淡、低模糊、小偏移，仅增强文字层次感 */
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
    /* 移动端阴影稍淡，避免占视觉焦点 */
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.15);
  }
}

.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}
.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto 20px;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
    margin-bottom: 16px;
  }
}

.page-subtitle {
  font-size: 1.125rem;
  color: $text-light;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    padding: 0 16px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

// 视频切换按钮组样式
.video-switcher {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 30px;
  background: rgba($primary-color, 0.03);
  border-bottom: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 16px 24px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    gap: 8px;
  }
}

.video-switch-btn {
  padding: 10px 20px;
  background: $white;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 8px;
  color: $text-color;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    border-color: rgba($primary-color, 0.5);
    background: rgba($primary-color, 0.05);
    transform: translateY(-2px);
  }

  &.active {
    background: $primary-color;
    color: $white;
    border-color: $primary-color;
    box-shadow: 0 4px 8px rgba($primary-color, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 0.8125rem;
  }
}

// 视频播放器区域
.video-player-section {
  background: $white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
}

.video-player-container {
  width: 100%;
}

// 视频包装器
.video-wrapper {
  position: relative;
  width: 100%;
  background: #000;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.company-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}

// 视频加载状态
.video-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10;

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba($white, 0.1);
    border-top-color: $white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  .loading-text {
    color: $white;
    font-size: 1rem;
  }
}

// 视频封面
.video-cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, rgba($primary-color, 0.9), rgba($primary-color-2, 0.8));
  cursor: pointer;
  z-index: 20;
  transition: opacity 0.3s ease;

  &:hover {
    .play-button {
      transform: scale(1.1);
    }
  }
}

.cover-content {
  text-align: center;
}

.play-button {
  margin-bottom: 20px;
  transition: transform 0.3s ease;
}

.play-hint {
  color: $white;
  font-size: 1.125rem;
  font-weight: 500;
  letter-spacing: 1px;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
}

// 视频控制遮罩
.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 5;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  .video-overlay:hover & {
    opacity: 1;
  }
}

.play-button-overlay {
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.8);
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
}

// 视频信息
.video-info {
  padding: 30px;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
}

.video-title {
  font-family: $font-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: $primary-color-2;
  margin-bottom: 16px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.375rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.video-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 16px;
    margin-bottom: 24px;
  }
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $text-light;
  font-size: 0.9375rem;

  .el-icon {
    color: $primary-color;
    font-size: 1.125rem;
  }
}

// 控制按钮
.control-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 12px;
  }
}

.control-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: $gray-10;
  border: 1px solid rgba($primary-color, 0.1);
  border-radius: 8px;
  color: $text-color;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  .el-icon {
    font-size: 1.125rem;
    color: $primary-color;
  }

  &:hover {
    background: rgba($primary-color, 0.05);
    border-color: rgba($primary-color, 0.3);
    transform: translateY(-2px);
  }

  &.active {
    background: $primary-color;
    border-color: $primary-color;
    color: $white;

    .el-icon {
      color: $white;
    }
  }

  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.875rem;

    .el-icon {
      font-size: 1rem;
    }
  }
}

// 富文本内容样式
:deep(.richtext-content) {
  padding: 0 30px 30px;
  line-height: 1.8;

  // 基础段落样式
  p {
    //margin-bottom: 1.5em;
    color: $gray-80;
    font-size: 1.125rem;
    text-align: justify;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    padding: 0 24px 24px;

    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 0 20px 20px;

    p {
      font-size: 0.9375rem;
    }
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.error-icon {
  margin-bottom: 20px;
  color: #f56c6c;
}

.error-title {
  font-size: 1.5rem;
  color: #f56c6c;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.retry-button {
  background: $primary-color;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-light-3;
    transform: translateY(-2px);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: $white;
  border-radius: 16px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
  }
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
}
</style>